/**
 * v0.10 policy diagnostics for the LSP — the in-editor half of P1b-3.
 *
 * Collects every `policy` declaration across the package's parsed files, builds
 * the selector/expression evaluation context from the cascaded model + derived
 * graph, and maps the active (non-waived) `error`/`warning` findings to engine
 * `Diagnostic`s anchored at the violation site. Advisory findings are report
 * artifacts, not diagnostics (design §4), so the engine mapper drops them.
 *
 * The result is merged into the package's diagnostic stream in
 * `Workspace.recomputeFromState`, so findings publish and route to the right
 * file by `source.file` exactly like any other diagnostic. A policy that uses a
 * not-yet-supported selector/expression feature is skipped silently in-editor
 * (surfacing it as a hint is a later slice).
 */

import {
  buildNodeUniverse,
  buildProcessLatencyResolver,
  buildProcessTestResolver,
  buildClosure,
  collectScopedPolicies,
  policyDiagnostics,
  policyLints,
  usePolicyDiagnostics,
  type Diagnostic,
  type DependencyGraph,
  type EvalContext,
  type PackageMap,
  type ParsedFile,
  type PolicyDecl,
  type ResolvedModel,
  type UsePolicyStmt,
} from "@archlang/engine";

/** Compute policy diagnostics for one package's cascaded model + graph.
 *  `deps` is the package's resolved dependency map — the vocabulary a
 *  `use policy <Name> from <pkg>` import resolves against. */
export function computePolicyDiagnostics(
  cascaded: ResolvedModel,
  graph: DependencyGraph | null,
  files: Iterable<ParsedFile>,
  deps: PackageMap = new Map(),
): Diagnostic[] {
  const fileList = [...files];
  const topLevel: PolicyDecl[] = [];
  const usePolicies: UsePolicyStmt[] = [];
  for (const file of fileList) {
    for (const d of file.declarations) {
      if (d.kind === "policy") topLevel.push(d.decl);
      else if (d.kind === "usePolicy") usePolicies.push(d.decl);
    }
  }
  // v0.10 §5.2: module-body policies join from the resolved tree, `ownerPath`
  // stamped (subtree scope); `in <Module>`-placed ones resolve their target
  // here (dangling target → IN_TARGET_NOT_FOUND, surfaced with the findings).
  const collected = collectScopedPolicies(cascaded, topLevel);
  const policies = collected.policies;
  const byName = new Map<string, PolicyDecl>();
  for (const p of policies) {
    if (!byName.has(p.name)) byName.set(p.name, p); // resolves `violating <P>` cross-references
  }
  if (policies.length === 0 && usePolicies.length === 0) return [...collected.diagnostics];

  const ctx: EvalContext = {
    universe: buildNodeUniverse(cascaded),
    graph: graph ?? undefined,
    closure: graph ? buildClosure(graph) : undefined,
    policies: byName,
    processLatency: buildProcessLatencyResolver(cascaded),
    processTest: buildProcessTestResolver(cascaded),
  };
  // Local violation findings (skip un-evaluable policies rather than crashing).
  const out = [...collected.diagnostics, ...policyDiagnostics(policies, ctx, { onError: () => undefined })];
  // Imported governance (§4): `use policy <Name> from <pkg>` instantiates an
  // exported policy over THIS package's model + graph (timeless, like the local
  // path). Resolution failures + active violations both surface as diagnostics.
  if (usePolicies.length > 0) out.push(...usePolicyDiagnostics(usePolicies, deps, ctx));
  // Hygiene lints ON each policy (dead waivers, subject-ignoring requires). Guard
  // per policy — one that can't evaluate simply contributes no lints.
  for (const policy of policies) {
    try {
      out.push(...policyLints(policy, ctx));
    } catch {
      /* un-evaluable policy → no lints */
    }
  }
  return out;
}
